import { createTheme } from '@mui/material/styles';

// =============================================================================
// Nexora – Digital-First Liechtenstein Fintech
// Dark, vibrant, modern. Electric purple-to-cyan gradients.
// Bold geometry, high contrast — fintech disruptor.
// =============================================================================

const neobankTheme = createTheme({
  palette: {
    mode: 'dark',
    logo: 'dark',
    bank: {
      name: 'Nexora',
      logo: '/bank-logos/nexora-dark.png',
    },
    primary: {
      main: '#7c3aed',
      dark: '#6d28d9',
      light: '#a78bfa',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#06b6d4',
      dark: '#0891b2',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0f0a1e',
      paper: '#221a3a',
      surface: '#1a1230',
      gradient: 'linear-gradient(135deg, #0f0a1e 0%, #1a0830 40%, #0f1a2e 70%, #0f0a1e 100%)',
    },
    text: {
      primary: '#f5f3ff',
      secondary: '#c4b5fd',
      disabled: '#8b7fc0',
      hint: '#9f8fcc',
      dark: '#0f0a1e',
      body: '#4a4060',
      heading: '#1e1535',
    },
    success: {
      main: '#10b981',
      light: '#d1fae5',
      dark: '#065f46',
      border: '#a7f3d0',
    },
    error: {
      main: '#ef4444',
      light: '#fef2f2',
    },
    grey: {
      50: '#1a1230',
      100: '#221a3a',
      200: '#2e2450',
      300: '#3b2d60',
      400: '#5b4f7a',
      500: '#7a6fa0',
      600: '#8b7fc0',
      700: '#a89cd0',
      800: '#c4b5fd',
      900: '#f5f3ff',
    },
    divider: 'rgba(124, 58, 237, 0.12)',
    action: {
      hover: 'rgba(124, 58, 237, 0.10)',
    },
    brand: {
      cyan: '#7c3aed',
      cyanHover: '#6d28d9',
      cyanLight: '#a78bfa',
      navy: '#0f0a1e',
      navyLight: '#1a1230',
      navyMid: '#160e2a',
      dialogBg: '#221a3a',
    },
    border: {
      light: '#2e2450',
      medium: '#3b2d60',
      dark: '#4a3a70',
      input: '#5b4f7a',
      muted: '#3b2d60',
      tableCell: 'rgba(124, 58, 237, 0.12)',
      onDark: 'rgba(124, 58, 237, 0.10)',
      onDarkAlt: 'rgba(124, 58, 237, 0.15)',
    },
    voice: {
      initBg1: '#1e1065',
      initBg2: '#2e1a80',
      initBg3: '#4c2ca0',
      thinkBg1: '#1a2040',
      thinkBg2: '#1e2850',
      thinkBg3: '#223060',
      speakBg1: '#0a2520',
      speakBg2: '#0e3530',
      speakBg3: '#124540',
      overlayBg: 'linear-gradient(180deg, #0f0a1e 0%, #1a0830 40%, #0f1a2e 70%, #0f0a1e 100%)',
    },
    gradient: {
      avatar: 'linear-gradient(135deg, #7c3aed 0%, #06b6d4 50%, #ec4899 100%)',
      voiceButton: 'linear-gradient(135deg, #1a1230 0%, #2e1065 50%, #1a1230 100%)',
      voiceTooltip: 'linear-gradient(135deg, #7c3aed 0%, #06b6d4 100%)',
    },
  },
  typography: {
    fontFamily: "'Space Grotesk', sans-serif",
    h1: { fontSize: '56px', fontWeight: 700, letterSpacing: '-1px' },
    h2: { fontSize: '28px', fontWeight: 700 },
    h3: { fontSize: '24px', fontWeight: 600 },
    h4: { fontSize: '21px', fontWeight: 600 },
    h5: { fontSize: '18px', fontWeight: 600 },
    h6: { fontSize: '16px', fontWeight: 600 },
    body1: { fontSize: '14px', fontWeight: 400 },
    body2: { fontSize: '13px', fontWeight: 400 },
    caption: { fontSize: '12px', fontWeight: 400 },
    overline: { fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px' },
    button: { fontSize: '15px', fontWeight: 600, textTransform: 'none' },
  },
  shape: {
    borderRadius: 12,
    radiusSmall: 6,
    radiusMedium: 8,
    radiusLarge: 14,
    radiusXl: 16,
    radiusXxl: 20,
    radiusPill: 28,
  },
  shadows: [
    'none',
    '0 1px 4px rgba(124, 58, 237, 0.08)',
    '0 4px 16px rgba(124, 58, 237, 0.20)',
    '0 2px 8px rgba(124, 58, 237, 0.12)',
    '0 4px 12px rgba(0, 0, 0, 0.40)',
    '0 8px 32px rgba(124, 58, 237, 0.25)',
    '0 2px 12px rgba(0, 0, 0, 0.30)',
    '0 4px 24px rgba(239, 68, 68, 0.35)',
    '0 8px 32px rgba(239, 68, 68, 0.45)',
    '0 0 80px rgba(124, 58, 237, 0.30)',
    '0 0 120px rgba(124, 58, 237, 0.20)',
    'inset 0 0 40px rgba(167, 139, 250, 0.30)',
    ...Array(13).fill('none'),
  ],
  spacing: 8,
  breakpoints: {
    values: { xs: 0, sm: 600, md: 768, lg: 1068, xl: 1536 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          fontFamily: "'Space Grotesk', sans-serif",
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, textTransform: 'none', fontFamily: "'Space Grotesk', sans-serif" },
        containedPrimary: {
          background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
          '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)' },
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: { fontFamily: "'Space Grotesk', sans-serif" },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: '#221a3a',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: '#221a3a',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          background: '#221a3a',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#7c3aed', height: 3, borderRadius: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "'Space Grotesk', sans-serif",
          '&.Mui-selected': { color: '#a78bfa' },
        },
      },
    },
  },
});

export default neobankTheme;
